import { useLocation } from "wouter";
import { useLayout } from "@/hooks/use-layout";
import { useAuth } from "@/hooks/use-auth";
import { GuideSidebar, PrevNext } from "./shared";

import OverviewSection from "./sections/overview";
import QuickStartSection from "./sections/quick-start";
import GlossarySection from "./sections/glossary";

import RolesSection from "./sections/roles";
import AccountSection from "./sections/account";
import LifecycleSection from "./sections/lifecycle";
import LanesSection from "./sections/lanes";
import ResponsibilitySection from "./sections/responsibility";
import ConceptsHandoffDeadlinesSection from "./sections/concepts-handoff-deadlines";
import SubTasksSection from "./sections/sub-tasks";
import SignOffSection from "./sections/sign-off";
import UpwardDispatchSection from "./sections/upward-dispatch";
import SoftDeleteSection from "./sections/soft-delete";
import NotificationsSection from "./sections/notifications";

import FormsRegisterSection from "./sections/forms-register";
import FormsUpdateSection from "./sections/forms-update";
import FormsHandoffSection from "./sections/forms-handoff";
import FormsReturnSection from "./sections/forms-return";
import FormsMarkDispatchedSection from "./sections/forms-mark-dispatched";
import FormsApproveCloseSection from "./sections/forms-approve-close";
import FormsSubTaskSection from "./sections/forms-sub-task";
import FormsMeetingTasksSection from "./sections/forms-meeting-tasks";
import FormsSendForReviewSection from "./sections/forms-send-for-review";
import FormsPasswordSection from "./sections/forms-password";

import PagesSummarySection from "./sections/pages-summary";
import PagesOriginsSection from "./sections/pages-origins";
import PagesRegisterSection from "./sections/pages-register";
import PagesUpdatesSection from "./sections/pages-updates";
import PagesMeetingTasksSection from "./sections/pages-meeting-tasks";
import PagesMdMeetingsSection from "./sections/pages-md-meetings";
import PagesHrDossierSection from "./sections/pages-hr-dossier";
import PagesEscalationsSection from "./sections/pages-escalations";
import PagesDetailSection from "./sections/pages-detail";

import AdminTabsSection from "./sections/admin-tabs";
import SettingsTabsSection from "./sections/settings-tabs";
import AgentApiSection from "./sections/agent-api";

import AdminKpiSection from "./sections/admin-kpi";
import AdminTeamMembersSection from "./sections/admin-team-members";
import AdminUserAccountsSection from "./sections/admin-user-accounts";
import AdminDepartmentsSection from "./sections/admin-departments";
import AdminVersionsSection from "./sections/admin-versions";
import AdminLanesSection from "./sections/admin-lanes";
import AdminActorsSection from "./sections/admin-actors";
import AdminReferenceListsSection from "./sections/admin-reference-lists";
import AdminWorkflowRulesSection from "./sections/admin-workflow-rules";
import AdminFieldPermissionsSection from "./sections/admin-field-permissions";
import AdminTransitionPermissionsSection from "./sections/admin-transition-permissions";
import AdminRolesPermissionsSection from "./sections/admin-roles-permissions";
import AdminReminderScheduleSection from "./sections/admin-reminder-schedule";
import AdminDeletedItemsSection from "./sections/admin-deleted-items";

import SettingsEmailSection from "./sections/settings-email";
import SettingsBrandingSection from "./sections/settings-branding";
import SettingsBackupSection from "./sections/settings-backup";
import SettingsApiKeysSection from "./sections/settings-api-keys";
import SettingsLiveRefreshSection from "./sections/settings-live-refresh";
import SettingsSystemLogSection from "./sections/settings-system-log";
import SettingsAlertDispatcherSection from "./sections/settings-alert-dispatcher";

import StatusReferenceSection from "./sections/status-reference";
import EventReferenceSection from "./sections/event-reference";
import WhatsNewSection from "./sections/whats-new";

const ROUTES: Record<string, () => JSX.Element> = {
  "": OverviewSection,
  "quick-start": QuickStartSection,
  "glossary": GlossarySection,

  "roles": RolesSection,
  "account": AccountSection,
  "lifecycle": LifecycleSection,
  "lanes": LanesSection,
  "responsibility": ResponsibilitySection,
  "handoff-deadlines": ConceptsHandoffDeadlinesSection,
  "sub-tasks": SubTasksSection,
  "sign-off": SignOffSection,
  "upward-dispatch": UpwardDispatchSection,
  "soft-delete": SoftDeleteSection,
  "notifications": NotificationsSection,

  "forms/register": FormsRegisterSection,
  "forms/update": FormsUpdateSection,
  "forms/handoff": FormsHandoffSection,
  "forms/return": FormsReturnSection,
  "forms/mark-dispatched": FormsMarkDispatchedSection,
  "forms/approve-close": FormsApproveCloseSection,
  "forms/sub-task": FormsSubTaskSection,
  "forms/meeting-tasks": FormsMeetingTasksSection,
  "forms/send-for-review": FormsSendForReviewSection,
  "forms/password": FormsPasswordSection,

  "pages/summary": PagesSummarySection,
  "pages/origins": PagesOriginsSection,
  "pages/register": PagesRegisterSection,
  "pages/updates": PagesUpdatesSection,
  "pages/meeting-tasks": PagesMeetingTasksSection,
  "pages/md-meetings": PagesMdMeetingsSection,
  "pages/hr-dossier": PagesHrDossierSection,
  "pages/escalations": PagesEscalationsSection,
  "pages/detail": PagesDetailSection,

  "admin-tabs": AdminTabsSection,
  "settings-tabs": SettingsTabsSection,
  "agent-api": AgentApiSection,

  "admin/kpi": AdminKpiSection,
  "admin/team-members": AdminTeamMembersSection,
  "admin/user-accounts": AdminUserAccountsSection,
  "admin/departments": AdminDepartmentsSection,
  "admin/versions": AdminVersionsSection,
  "admin/lanes": AdminLanesSection,
  "admin/actors": AdminActorsSection,
  "admin/reference-lists": AdminReferenceListsSection,
  "admin/workflow-rules": AdminWorkflowRulesSection,
  "admin/field-permissions": AdminFieldPermissionsSection,
  "admin/transition-permissions": AdminTransitionPermissionsSection,
  "admin/roles-permissions": AdminRolesPermissionsSection,
  "admin/reminder-schedule": AdminReminderScheduleSection,
  "admin/deleted-items": AdminDeletedItemsSection,

  "settings/email": SettingsEmailSection,
  "settings/branding": SettingsBrandingSection,
  "settings/backup": SettingsBackupSection,
  "settings/api-keys": SettingsApiKeysSection,
  "settings/live-refresh": SettingsLiveRefreshSection,
  "settings/system-log": SettingsSystemLogSection,
  "settings/alert-dispatcher": SettingsAlertDispatcherSection,

  "status-reference": StatusReferenceSection,
  "event-reference": EventReferenceSection,
  "whats-new": WhatsNewSection,
};

function NotFound({ slug }: { slug: string }) {
  return (
    <div data-testid="guide-not-found">
      <h1 className="text-xl font-bold text-white mb-2">Page not found</h1>
      <p className="text-sm text-white/55">No guide topic found at <span className="font-mono text-white">/guide/{slug}</span>. Pick a topic from the menu on the left.</p>
    </div>
  );
}

// Per-section permission gates: slug -> all permissions required.
const SECTION_GATES: Record<string, string[]> = {
  "admin/team-members": ["view_user_guide", "manage_team_members"],
};

function GatedContent({ slug, Component }: { slug: string; Component: React.ComponentType }) {
  const { hasPermission } = useAuth();
  const gates = SECTION_GATES[slug];
  if (gates && !gates.every(p => hasPermission(p))) {
    return (
      <div data-testid="guide-section-restricted">
        <h1 className="text-xl font-bold text-white mb-2">Restricted topic</h1>
        <p className="text-sm text-white/55">
          This guide topic is for administrators with team-member management
          access. Ask your super admin to grant the <span className="font-mono text-white">manage_team_members</span> permission, or pick a different topic from the menu.
        </p>
      </div>
    );
  }
  return <Component />;
}

export default function UserGuidePage() {
  const [location] = useLocation();
  const { containerClass } = useLayout();

  let slug = location.replace(/^\/guide\/?/, "").replace(/\/$/, "");
  if (slug.startsWith("/")) slug = slug.slice(1);

  const Component = ROUTES[slug] || (() => <NotFound slug={slug} />);

  return (
    <div className={`${containerClass} mx-auto p-6 pb-20`} data-testid="page-user-guide">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto pr-2">
          <GuideSidebar activeSlug={slug} />
        </aside>
        <div className="min-w-0">
          <GatedContent slug={slug} Component={Component} />
          <PrevNext slug={slug} />
        </div>
      </div>
    </div>
  );
}
